/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { LoggerService } from './logger.service';

/** Female Malayalam Piper voice (Indic TTS meera corpus). */
const MEERA_VOICE_BASE =
  'https://huggingface.co/rhasspy/piper-voices/resolve/main/ml/ml_IN/meera/medium/ml_IN-meera-medium.onnx';

/** Optional local fallback when HuggingFace CDN times out (place files under src/assets/tts/). */
const MEERA_VOICE_LOCAL_BASE = 'assets/tts/ml_IN-meera-medium.onnx';

/**
 * Clarity tuning for meera (only open-source ml_IN female Piper voice).
 * Pace aligned with en/ar SpeechSynthesis (utterance.rate 0.92).
 */
const MALAYALAM_VOICE_TUNING = {
  noiseScale: 0.44,
  lengthScale: 1.0,
  noiseW: 0.58,
  speechRate: 0.92,
} as const;

const SPEECH_CACHE_MAX = 12;
const PROGRESS_INTERVAL_MS = 75;

const ONNX_WASM_BASE =
  'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.22.0/dist/';
const ONNX_MODULE_URL =
  'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.22.0/dist/ort.bundle.min.mjs';
const PIPER_WASM_BASE =
  'https://cdn.jsdelivr.net/npm/@diffusionstudio/piper-wasm@1.0.0/build/piper_phonemize';

const MODEL_CACHE_DB = 'logibids-piper-tts';
const MODEL_CACHE_STORE = 'models';
const MODEL_CACHE_KEY = 'ml_IN-meera-medium-v2';

const MODEL_FETCH_RETRIES = 3;
const MODEL_FETCH_RETRY_DELAY_MS = 2000;

interface PiperModelConfig {
  audio: { sample_rate: number };
  espeak: { voice: string };
  inference: {
    noise_scale: number;
    length_scale: number;
    noise_w: number;
  };
  num_speakers?: number;
  speaker_id_map?: Record<string, number>;
}

interface PiperPhonemizeModule {
  callMain: (args: string[]) => void;
}

type OrtModule = {
  InferenceSession: typeof import('onnxruntime-web').InferenceSession;
  Tensor: typeof import('onnxruntime-web').Tensor;
  env: import('onnxruntime-web').Env;
};

export interface MalayalamPiperSpeakOptions {
  muted?: boolean;
  /** Fired before model load / synthesis (show loader). */
  onPreparing?: () => void;
  onStart?: () => void;
  onProgress?: (ratio: number) => void;
}

/**
 * Browser-side Piper TTS for Malayalam only (ml_IN-meera-medium).
 * English/Arabic continue to use SpeechSynthesis in ai-cab-booking.
 */
@Injectable({ providedIn: 'root' })
export class MalayalamPiperTtsService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly logger = inject(LoggerService);

  private initPromise: Promise<void> | null = null;
  private ortModule: OrtModule | null = null;
  private ortSession: Awaited<
    ReturnType<OrtModule['InferenceSession']['create']>
  > | null = null;
  private modelConfig: PiperModelConfig | null = null;
  private createPiperPhonemize:
    | ((opts: Record<string, unknown>) => Promise<PiperPhonemizeModule>)
    | null = null;

  private volumeMuted = false;
  private audio: HTMLAudioElement | null = null;
  private objectUrl: string | null = null;
  private progressInterval: ReturnType<typeof setInterval> | null = null;
  private speakGeneration = 0;
  private activePlayFinish: (() => void) | null = null;
  private readonly speechCache = new Map<string, Blob>();
  private readonly prefetchPromises = new Map<string, Promise<void>>();

  /** Returns true when Malayalam Piper TTS should be used instead of SpeechSynthesis. */
  isMalayalamLanguage(appLang: string): boolean {
    return appLang.trim().toLowerCase() === 'ml';
  }

  isSupported(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  isReady(): boolean {
    return !!(this.ortSession && this.modelConfig && this.createPiperPhonemize);
  }

  /** Pre-synthesizes speech while the welcome API loads (ml only). */
  prepareSpeech(text: string): Promise<void> {
    if (!this.isSupported()) {
      return Promise.resolve();
    }

    const key = text.trim();
    if (!key || this.speechCache.has(key)) {
      return Promise.resolve();
    }

    const inFlight = this.prefetchPromises.get(key);
    if (inFlight) {
      return inFlight;
    }

    const promise = this.ensureReady()
      .then(async () => {
        if (this.speechCache.has(key)) {
          return;
        }

        const blob = await this.synthesize(key);
        this.speechCache.set(key, blob);
        this.trimSpeechCache();
      })
      .catch(error => {
        this.logger.warn('Malayalam Piper prefetch failed', error);
      })
      .finally(() => {
        this.prefetchPromises.delete(key);
      });

    this.prefetchPromises.set(key, promise);
    return promise;
  }

  isPlaying(): boolean {
    return !!this.audio && !this.audio.paused && !this.audio.ended;
  }

  isPausedByMute(): boolean {
    return this.volumeMuted;
  }

  /** Mutes/unmutes playback without pausing — text sync and flow continue. */
  setVolumeMuted(muted: boolean): void {
    this.volumeMuted = muted;
    if (this.audio) {
      this.audio.muted = muted;
    }
  }

  isVolumeMuted(): boolean {
    return this.volumeMuted;
  }

  /** Preloads ONNX model and WASM runtimes (call when chat opens with lang=ml). */
  preload(): Promise<void> {
    if (!this.isSupported()) {
      return Promise.resolve();
    }
    return this.ensureReady();
  }

  /**
   * Synthesizes and plays Malayalam speech.
   * Resolves when playback completes or is stopped.
   */
  async speak(
    text: string,
    options: MalayalamPiperSpeakOptions = {}
  ): Promise<void> {
    if (!this.isSupported() || !text.trim()) {
      return;
    }

    const generation = ++this.speakGeneration;
    this.stopPlaybackInternal(false);

    try {
      options.onPreparing?.();

      await this.ensureReady();
      if (generation !== this.speakGeneration) {
        return;
      }

      const key = text.trim();
      let wavBlob = this.speechCache.get(key);
      if (!wavBlob) {
        wavBlob = await this.synthesize(key);
        if (generation !== this.speakGeneration) {
          return;
        }
        this.speechCache.set(key, wavBlob);
        this.trimSpeechCache();
      }

      await this.playBlob(wavBlob, generation, options);
    } catch (error) {
      this.logger.error('Malayalam Piper TTS failed', error);
      throw error;
    }
  }

  stop(): void {
    this.speakGeneration++;
    this.stopPlaybackInternal(true);
  }

  pause(): void {
    this.setVolumeMuted(true);
  }

  resume(): void {
    this.setVolumeMuted(false);
  }

  private async ensureReady(): Promise<void> {
    if (!this.isSupported()) {
      return;
    }

    if (this.ortSession && this.modelConfig && this.createPiperPhonemize) {
      return;
    }

    if (!this.initPromise) {
      this.initPromise = this.initialize().catch(err => {
        this.initPromise = null;
        throw err;
      });
    }

    await this.initPromise;
  }

  private async initialize(): Promise<void> {
    if (!this.isSupported()) {
      return;
    }

    const moduleUrl = new URL('assets/tts/piper-o91UDS6e.js', document.baseURI)
      .href;
    const piperWasm = (await import(
      /* webpackIgnore: true */ /* @vite-ignore */ moduleUrl
    )) as {
      createPiperPhonemize: (
        opts: Record<string, unknown>
      ) => Promise<PiperPhonemizeModule>;
    };
    this.createPiperPhonemize = piperWasm.createPiperPhonemize as (
      opts: Record<string, unknown>
    ) => Promise<PiperPhonemizeModule>;

    this.ortModule = (await import(
      /* webpackIgnore: true */ /* @vite-ignore */ ONNX_MODULE_URL
    )) as OrtModule;
    this.ortModule.env['allowLocalModels'] = false;
    // Single-threaded WASM is more reliable without cross-origin isolation headers.
    this.ortModule.env.wasm.numThreads = 1;
    this.ortModule.env.wasm.wasmPaths = ONNX_WASM_BASE;

    this.modelConfig = await this.loadModelConfig();
    const modelBuffer = await this.loadModelBuffer();
    this.ortSession = await this.ortModule.InferenceSession.create(modelBuffer);
  }

  private async loadModelConfig(): Promise<PiperModelConfig> {
    const configUrls = [
      `${MEERA_VOICE_BASE}.json`,
      new URL(`${MEERA_VOICE_LOCAL_BASE}.json`, document.baseURI).href,
    ];

    for (const url of configUrls) {
      try {
        const configRes = await fetch(url);
        if (!configRes.ok) {
          continue;
        }
        return (await configRes.json()) as PiperModelConfig;
      } catch (error) {
        this.logger.warn('Malayalam Piper config fetch failed', { url, error });
      }
    }

    throw new Error('Failed to load Malayalam Piper config');
  }

  private async loadModelBuffer(): Promise<ArrayBuffer> {
    const cached = await this.readModelFromCache();
    if (cached) {
      return cached;
    }

    const remoteUrls = [
      await this.resolveDownloadUrl(MEERA_VOICE_BASE),
      MEERA_VOICE_BASE,
    ];
    const localUrl = new URL(MEERA_VOICE_LOCAL_BASE, document.baseURI).href;
    const candidateUrls = [...new Set([...remoteUrls, localUrl])];
    let lastError: unknown;

    for (let attempt = 0; attempt < MODEL_FETCH_RETRIES; attempt++) {
      for (const url of candidateUrls) {
        try {
          const buffer = await this.fetchArrayBuffer(url);
          await this.writeModelToCache(buffer);
          return buffer;
        } catch (error) {
          lastError = error;
          this.logger.warn('Malayalam Piper model fetch attempt failed', {
            url,
            attempt: attempt + 1,
            error,
          });
        }
      }

      if (attempt < MODEL_FETCH_RETRIES - 1) {
        await this.delay(MODEL_FETCH_RETRY_DELAY_MS * (attempt + 1));
      }
    }

    throw lastError instanceof Error
      ? lastError
      : new Error('Failed to load Malayalam Piper model');
  }

  /** Follows HuggingFace redirects so the large ONNX file is fetched from hf.co CDN directly. */
  private async resolveDownloadUrl(url: string): Promise<string> {
    try {
      const headRes = await fetch(url, { method: 'HEAD', redirect: 'follow' });
      if (headRes.ok && headRes.url && headRes.url !== url) {
        return headRes.url;
      }
    } catch (error) {
      this.logger.warn('Malayalam Piper HEAD resolve failed', error);
    }

    return url;
  }

  private async fetchArrayBuffer(url: string): Promise<ArrayBuffer> {
    const response = await fetch(url, { redirect: 'follow' });
    if (!response.ok) {
      throw new Error(
        `Failed to load Malayalam Piper model (${response.status})`
      );
    }

    return response.arrayBuffer();
  }

  private async readModelFromCache(): Promise<ArrayBuffer | null> {
    if (!this.isIndexedDbAvailable()) {
      return null;
    }

    try {
      const db = await this.openModelCacheDb();
      const buffer = await new Promise<ArrayBuffer | null>(
        (resolve, reject) => {
          const tx = db.transaction(MODEL_CACHE_STORE, 'readonly');
          const request = tx
            .objectStore(MODEL_CACHE_STORE)
            .get(MODEL_CACHE_KEY);
          request.onsuccess = () => {
            resolve((request.result as ArrayBuffer | undefined) ?? null);
          };
          request.onerror = () =>
            reject(request.error ?? new Error('IndexedDB read failed'));
        }
      );
      db.close();

      if (buffer && buffer.byteLength > 0) {
        return buffer;
      }
    } catch (error) {
      this.logger.warn('Malayalam Piper model cache read failed', error);
    }

    return null;
  }

  private async writeModelToCache(buffer: ArrayBuffer): Promise<void> {
    if (!this.isIndexedDbAvailable()) {
      return;
    }

    try {
      const db = await this.openModelCacheDb();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(MODEL_CACHE_STORE, 'readwrite');
        tx.objectStore(MODEL_CACHE_STORE).put(buffer, MODEL_CACHE_KEY);
        tx.oncomplete = () => resolve();
        tx.onerror = () =>
          reject(tx.error ?? new Error('IndexedDB write failed'));
      });
      db.close();
    } catch (error) {
      this.logger.warn('Malayalam Piper model cache write failed', error);
    }
  }

  private openModelCacheDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(MODEL_CACHE_DB, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(MODEL_CACHE_STORE)) {
          db.createObjectStore(MODEL_CACHE_STORE);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () =>
        reject(request.error ?? new Error('IndexedDB open failed'));
    });
  }

  private isIndexedDbAvailable(): boolean {
    return typeof indexedDB !== 'undefined';
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async synthesize(text: string): Promise<Blob> {
    if (
      !this.ortSession ||
      !this.modelConfig ||
      !this.createPiperPhonemize ||
      !this.ortModule
    ) {
      throw new Error('Malayalam Piper TTS is not initialized');
    }

    const config = this.modelConfig;
    const ort = this.ortModule;
    const input = JSON.stringify([{ text: text.trim() }]);

    const phonemeIds = await new Promise<number[]>((resolve, reject) => {
      void this.createPiperPhonemize!({
        print: (data: string) => {
          try {
            resolve(JSON.parse(data).phoneme_ids as number[]);
          } catch (err) {
            reject(err);
          }
        },
        printErr: (message: string) => {
          reject(new Error(message));
        },
        locateFile: (url: string) => {
          if (url.endsWith('.wasm')) {
            return `${PIPER_WASM_BASE}.wasm`;
          }
          if (url.endsWith('.data')) {
            return `${PIPER_WASM_BASE}.data`;
          }
          return url;
        },
      }).then(module => {
        module.callMain([
          '-l',
          config.espeak.voice,
          '--input',
          input,
          '--espeak_data',
          '/espeak-ng-data',
        ]);
      });
    });

    const lengthScale =
      (config.inference.length_scale * MALAYALAM_VOICE_TUNING.lengthScale) /
      MALAYALAM_VOICE_TUNING.speechRate;
    const feeds: Record<string, InstanceType<OrtModule['Tensor']>> = {
      input: new ort.Tensor('int64', phonemeIds, [1, phonemeIds.length]),
      input_lengths: new ort.Tensor('int64', [phonemeIds.length]),
      scales: new ort.Tensor('float32', [
        MALAYALAM_VOICE_TUNING.noiseScale,
        lengthScale,
        MALAYALAM_VOICE_TUNING.noiseW,
      ]),
    };

    if (
      (config.num_speakers ?? Object.keys(config.speaker_id_map ?? {}).length) >
      1
    ) {
      feeds['sid'] = new ort.Tensor('int64', [0]);
    }

    const result = await this.ortSession.run(feeds);
    const pcm = this.normalizePcm(result['output']?.data as Float32Array);
    const sampleRate = config.audio.sample_rate;

    return new Blob([this.pcmToWav(pcm, 1, sampleRate)], {
      type: 'audio/x-wav',
    });
  }

  private trimSpeechCache(): void {
    while (this.speechCache.size > SPEECH_CACHE_MAX) {
      const oldest = this.speechCache.keys().next().value;
      if (oldest === undefined) {
        break;
      }
      this.speechCache.delete(oldest);
    }
  }

  /** Peak-normalizes PCM and applies gentle soft-clipping for clearer playback. */
  private normalizePcm(buffer: Float32Array): Float32Array {
    if (buffer.length === 0) {
      return buffer;
    }

    let peak = 0;
    for (const sample of buffer) {
      peak = Math.max(peak, Math.abs(sample));
    }

    if (peak <= 0.0001) {
      return buffer;
    }

    const gain = 0.97 / peak;
    const normalized = new Float32Array(buffer.length);
    for (let i = 0; i < buffer.length; i++) {
      const amplified = buffer[i] * gain;
      normalized[i] = Math.tanh(amplified * 1.12) / Math.tanh(1.12);
    }

    return normalized;
  }

  private playBlob(
    blob: Blob,
    generation: number,
    options: MalayalamPiperSpeakOptions
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.revokeObjectUrl();
      this.objectUrl = URL.createObjectURL(blob);
      const audio = new Audio(this.objectUrl);
      this.audio = audio;
      audio.muted = this.volumeMuted || !!options.muted;

      const finish = (): void => {
        if (this.activePlayFinish === finishPlay) {
          this.activePlayFinish = null;
        }
        this.clearProgressInterval();
        if (generation === this.speakGeneration) {
          this.cleanupAudio();
        }
        resolve();
      };

      const finishPlay = finish;
      this.activePlayFinish = finishPlay;

      audio.onloadedmetadata = () => {
        if (generation !== this.speakGeneration || this.audio !== audio) {
          finish();
          return;
        }
        options.onStart?.();
        this.startProgressTracking(audio, generation, options.onProgress);
      };

      audio.onended = () => {
        if (generation !== this.speakGeneration || this.audio !== audio) {
          finish();
          return;
        }
        options.onProgress?.(1);
        finish();
      };

      audio.onerror = () => {
        if (this.activePlayFinish === finishPlay) {
          this.activePlayFinish = null;
        }
        this.clearProgressInterval();
        if (generation === this.speakGeneration) {
          this.cleanupAudio();
        }
        reject(new Error('Malayalam Piper audio playback failed'));
      };

      void audio
        .play()
        .then(() => {
          if (generation !== this.speakGeneration || this.audio !== audio) {
            audio.pause();
            audio.removeAttribute('src');
          }
        })
        .catch(err => {
          if (this.activePlayFinish === finishPlay) {
            this.activePlayFinish = null;
          }
          this.logger.warn('Malayalam Piper play() failed', err);
          reject(err);
        });
    });
  }

  private startProgressTracking(
    audio: HTMLAudioElement,
    generation: number,
    onProgress?: (ratio: number) => void
  ): void {
    if (!onProgress) {
      return;
    }

    this.clearProgressInterval();
    this.progressInterval = setInterval(() => {
      if (generation !== this.speakGeneration || !this.audio) {
        this.clearProgressInterval();
        return;
      }

      const duration = audio.duration;
      if (!Number.isFinite(duration) || duration <= 0) {
        return;
      }

      onProgress(Math.min(1, audio.currentTime / duration));
    }, PROGRESS_INTERVAL_MS);
  }

  private stopPlaybackInternal(_resetMuteFlag: boolean): void {
    this.clearProgressInterval();
    if (this.audio) {
      const audio = this.audio;
      audio.onloadedmetadata = null;
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
      audio.currentTime = 0;
      audio.removeAttribute('src');
      audio.load();
    }
    this.cleanupAudio();
    this.activePlayFinish?.();
    this.activePlayFinish = null;
  }

  private cleanupAudio(): void {
    this.revokeObjectUrl();
    this.audio = null;
  }

  private revokeObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }

  private clearProgressInterval(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  private pcmToWav(
    buffer: Float32Array,
    numChannels: number,
    sampleRate: number
  ): ArrayBuffer {
    const bufferLength = buffer.length;
    const headerLength = 44;
    const view = new DataView(
      new ArrayBuffer(bufferLength * numChannels * 2 + headerLength)
    );

    view.setUint32(0, 0x46464952, true);
    view.setUint32(4, view.buffer.byteLength - 8, true);
    view.setUint32(8, 0x45564157, true);
    view.setUint32(12, 0x20746d66, true);
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, numChannels * 2 * sampleRate, true);
    view.setUint16(32, numChannels * 2, true);
    view.setUint16(34, 16, true);
    view.setUint32(36, 0x61746164, true);
    view.setUint32(40, 2 * bufferLength, true);

    let offset = headerLength;
    for (let i = 0; i < bufferLength; i++) {
      const sample = buffer[i];
      if (sample >= 1) {
        view.setInt16(offset, 32767, true);
      } else if (sample <= -1) {
        view.setInt16(offset, -32768, true);
      } else {
        view.setInt16(offset, (sample * 32768) | 0, true);
      }
      offset += 2;
    }

    return view.buffer;
  }
}
